// Cliente del módulo de inventario → `/api/v1/admin/inventory`.

import { ensureFreshToken } from '@/lib/tokenRefresh';

import type {
  ExpiringLot,
  Movement,
  Stock,
  Supply,
  SupplyRequest,
  WasteReason,
} from './types';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/admin/inventory';

export class InventoryApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'InventoryApiError';
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  const orgId = localStorage.getItem('org_id') || '';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (orgId) headers['X-Org-Id'] = orgId;
  return headers;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = await authHeaders();
  if (init.body) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${DJANGO_PROXY}${BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body?.detail || detail;
    } catch {
      /* sin cuerpo JSON */
    }
    throw new InventoryApiError(detail, res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const inventoryApi = {
  supplies: (): Promise<Supply[]> => request('/supplies'),
  stock: (locationId: string): Promise<Stock[]> =>
    request(`/stock?location_id=${encodeURIComponent(locationId)}`),
  expiring: (dias = 7): Promise<ExpiringLot[]> => request(`/lots?dias=${dias}`),
  movements: (params: { kind?: string; status?: string } = {}): Promise<Movement[]> => {
    const qs = new URLSearchParams();
    if (params.kind) qs.set('kind', params.kind);
    if (params.status) qs.set('status', params.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/movements${suffix}`);
  },
  requests: (status = 'open'): Promise<SupplyRequest[]> =>
    request(`/requests?status=${status}`),
  wasteReasons: (): Promise<WasteReason[]> => request('/waste-reasons'),

  // ── Escritura ─────────────────────────────────────────────────

  createSupply: (body: {
    name: string;
    slug: string;
    unit: string;
    tracks_expiry: boolean;
  }): Promise<Supply> =>
    request('/supplies', { method: 'POST', body: JSON.stringify(body) }),

  setLevel: (body: {
    supply_id: string;
    location_id: string;
    reorder_point: string;
    target_low_day: string;
    target_high_day: string;
  }): Promise<unknown> => request('/levels', { method: 'PUT', body: JSON.stringify(body) }),

  entry: (body: {
    supply_id: string;
    location_id: string;
    quantity: string;
    expires_on: string | null;
    note?: string;
  }): Promise<Movement> =>
    request('/entries', { method: 'POST', body: JSON.stringify(body) }),

  exit: (body: {
    supply_id: string;
    location_id: string;
    quantity: string;
    kind: string;
    waste_reason_id?: string | null;
    photo_url?: string;
    note?: string;
  }): Promise<Movement> => request('/exits', { method: 'POST', body: JSON.stringify(body) }),

  transfer: (body: {
    supply_id: string;
    origin_id: string;
    destination_id: string;
    quantity: string;
    note?: string;
  }): Promise<Movement> =>
    request('/transfers', { method: 'POST', body: JSON.stringify(body) }),

  confirmTransfer: (movementId: string, confirmedBy: string): Promise<Movement> =>
    request(`/transfers/${movementId}/confirm`, {
      method: 'POST',
      body: JSON.stringify({ confirmed_by: confirmedBy }),
    }),
};
